/**
 * Series Color Utilities
 * Centralized color mapping for chart series and pricing displays
 */

/**
 * Get computed value of a CSS custom property
 */
export function getThemeColor(variable: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(variable)
    .trim();
  return value || fallback;
}

/**
 * Generate a consistent color from a string using hashing
 * Used as fallback for series not in the predefined color map
 */
export function generateColorFromString(str: string): string {
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Generate HSL color with fixed saturation and lightness for consistency
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 65%, 55%)`;
}

/**
 * Get color for a series based on its name
 * Maps known series to theme colors, generates color for unknown series
 */
export function getSeriesColor(name: string): string {
  // Predefined color map for known series
  const colorMap: Record<string, string> = {
    'Us index': '--color-blue-500',
    Hyperscalers: '--color-blue-400',
    'Major gpu clouds': '--color-green-500',
    Serverless: '--color-orange-500',
    'Specialized clouds': '--color-red-600',
    Other: '--color-text-secondary',
  };

  // Return theme color if series is known
  if (colorMap[name]) {
    return getThemeColor(colorMap[name], '#858585');
  }

  // Generate a consistent color for unknown series
  return generateColorFromString(name);
}
